// ─── Equipment occurrence engine ─────────────────────────────────────────────
//
// Pure, dependency-free expansion of per-Equipment-Type schedules into dated
// occurrences for a calendar range (mirrors the style of src/lib/scheduling).
// Keep this module free of React/network/side-effect imports so it stays
// unit-testable in isolation.
//
// Expansion rules (deterministic, GMP business-day based, per the planner deck):
//   daily          → every business day (Mon–Fri)
//   weekly         → N days/week parsed from the cell note ("2×/wk …" → Mon+Thu);
//                    defaults to Monday when no count is present
//   biweekly       → every other Monday (stable ISO-week parity)
//   monthly        → first business day of each month
//   quarterly      → first business day of Jan / Apr / Jul / Oct
//   semiannually   → first business day of Jan / Jul
//   yearly         → first business day of a month derived stably from the
//                    equipment-type name (spreads yearly events across the year)
//   calendar       → non-routine events pass through on their exact dates

import {
  type EquipmentGroup,
  type EquipmentTypeRow,
  type EventType,
} from '@/types/equipment'

export type PlannerOccurrence = {
  id: string
  /** ISO date, e.g. "2026-10-01". */
  date: string
  equipmentType: string
  group: EquipmentGroup
  /** The routine event type code, or the mapped color type for non-routine events. */
  eventType: EventType
  /** Display label, e.g. "Preventive Maintenance (PM)" or "HEPA Replacement". */
  label: string
  /** Hex color resolved from the event-type catalog. */
  color: string
  /** True for one-off calendar-based (non-routine) events. */
  nonRoutine: boolean
  note?: string
  /** Optional time of day (HH:mm 24h), carried from the schedule cell. */
  startTime?: string
  /** Optional window end time (HH:mm 24h). */
  endTime?: string

  // ─── Repeating-task paints only ────────────────────────────────────────────
  //
  // A schedule occurrence is per TYPE and leaves these undefined: it means
  // "applies to all N units", and no task exists behind it until generation
  // creates one. A repeating task is the opposite — one real MaintenanceEvent
  // on ONE machine, painted at its own cadence — so these being set is exactly
  // what tells the dialog not to talk about generation.
  /** The MaintenanceEvent this paint belongs to. */
  eventId?: string
  equipmentId?: string
  /** Unit label, e.g. "BSC-01". */
  equipmentLabel?: string
  /** The task's cadence, so the calendar can say "repeats weekly". */
  repeatCycle?: string
}

/**
 * Human-readable time text for an occurrence: "8:00 AM", "8:00–10:00 AM", or
 * empty string when the occurrence is all-day (no startTime).
 */
export function formatOccurrenceTime(occ: {
  startTime?: string
  endTime?: string
}): string {
  if (!occ.startTime) return ''
  const fmt = (hhmm: string): string => {
    const [h, m] = hhmm.split(':').map(Number)
    const period = h < 12 ? 'AM' : 'PM'
    const hour12 = h % 12 === 0 ? 12 : h % 12
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`
  }
  if (!occ.endTime) return fmt(occ.startTime)
  return `${fmt(occ.startTime)} – ${fmt(occ.endTime)}`
}

/** Color/category mapping for the predefined non-routine event catalog. */
export const NON_ROUTINE_EVENT_TYPE: Record<string, EventType> = {
  'Annual Calibration': 'calibration',
  'Preventive Maintenance (Vendor)': 'preventive_maintenance',
  'Vendor PM visit': 'preventive_maintenance',
  'HEPA Replacement': 'corrective_maintenance',
  'Requalification (IQ/OQ/PQ)': 'performance_qualification',
  'Safety Inspection': 'inspection',
  'Firmware Update': 'verification',
  'Decommissioning': 'corrective_maintenance',
}

// ─── Date helpers (UTC, string-in string-out) ───────────────────────────────

const DAY_MS = 86_400_000

function parseISO(date: string): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function toISO(ts: number): string {
  const d = new Date(ts)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** 0 = Sunday … 6 = Saturday. */
function dayOfWeek(ts: number): number {
  return new Date(ts).getUTCDay()
}

function isBusinessDay(ts: number): boolean {
  const dow = dayOfWeek(ts)
  return dow >= 1 && dow <= 5
}

/** First business day of a month, as a timestamp. */
function firstBusinessDay(year: number, month: number): number {
  let ts = Date.UTC(year, month, 1)
  while (!isBusinessDay(ts)) ts += DAY_MS
  return ts
}

/** Stable non-negative hash for spreading yearly events across months. */
function hashString(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}

/** Stable week parity for bi-weekly cycles (weeks counted from the epoch Monday). */
function isEvenWeek(ts: number): boolean {
  const daysSinceEpoch = Math.floor(ts / DAY_MS)
  // 1970-01-01 was a Thursday; shift so weeks start on Monday.
  const weeksSinceMonday = Math.floor((daysSinceEpoch + 3) / 7)
  return weeksSinceMonday % 2 === 0
}

/** Days-of-week sets for "N×/wk" weekly schedules (business days, spread out). */
const WEEKLY_DAY_SETS: Record<number, number[]> = {
  1: [1], // Mon
  2: [1, 4], // Mon, Thu
  3: [1, 3, 5], // Mon, Wed, Fri
  4: [1, 2, 4, 5], // Mon, Tue, Thu, Fri
  5: [1, 2, 3, 4, 5],
}

/** Parse the per-week count out of a cell note like "2×/wk + M" or "3x/wk". */
export function weeklyCountFromNote(note?: string): number {
  if (!note) return 1
  const match = note.match(/(\d+)\s*[×x]\s*\/\s*wk/i)
  if (!match) return 1
  const n = Number(match[1])
  return Math.min(5, Math.max(1, n))
}

// ─── Expansion ──────────────────────────────────────────────────────────────

/**
 * Expand per-type schedules + non-routine calendar events into dated
 * occurrences within [rangeStart, rangeEnd) — end exclusive, matching the
 * range FullCalendar reports for a view.
 */
export function expandOccurrences(
  types: EquipmentTypeRow[],
  rangeStart: string,
  rangeEnd: string,
): PlannerOccurrence[] {
  const startTs = parseISO(rangeStart)
  const endTs = parseISO(rangeEnd)
  if (endTs <= startTs) return []

  const out: PlannerOccurrence[] = []

  const push = (
    ts: number,
    row: EquipmentTypeRow,
    eventType: EventType,
    label: string,
    nonRoutine: boolean,
    note?: string,
  ) => {
    const date = toISO(ts)
    out.push({
      id: `${row.equipmentType}|${eventType}|${label}|${date}`,
      date,
      equipmentType: row.equipmentType,
      group: row.group,
      eventType,
      label,
      // Catalog colors are resolved by the consumer; this dead-path module keeps a neutral default.
      color: '#64748b',
      nonRoutine,
      note,
    })
  }

  for (const row of types) {
    // A type nobody services produces nothing — no routine cycle and no
    // non-routine event. The backend filters on `maintained` before it expands
    // anything (`listOccurrences`, `generateEvents`), and a preview that
    // disagreed would advertise maintenance that will never be generated.
    if (!row.maintained) continue

    // Routine cycles → one occurrence per event type per matching day.
    for (let ts = startTs; ts < endTs; ts += DAY_MS) {
      const dow = dayOfWeek(ts)
      const business = dow >= 1 && dow <= 5

      const emitCycle = (cycle: keyof typeof row.schedule) => {
        const entry = row.schedule[cycle]
        if (!entry) return
        for (const type of entry.eventTypes) {
          push(ts, row, type, type, false, entry.note)
        }
      }

      if (business && row.schedule.daily) emitCycle('daily')

      if (row.schedule.weekly) {
        const days = WEEKLY_DAY_SETS[weeklyCountFromNote(row.schedule.weekly.note)]
        if (days.includes(dow)) emitCycle('weekly')
      }

      if (row.schedule.biweekly && dow === 1 && isEvenWeek(ts)) emitCycle('biweekly')
    }

    // Month-anchored cycles → first business day of their months.
    const startYear = new Date(startTs).getUTCFullYear()
    const endYear = new Date(endTs - DAY_MS).getUTCFullYear()
    for (let year = startYear; year <= endYear; year++) {
      for (let month = 0; month < 12; month++) {
        const anchor = firstBusinessDay(year, month)
        if (anchor < startTs || anchor >= endTs) continue

        const emitAnchored = (cycle: keyof typeof row.schedule) => {
          const entry = row.schedule[cycle]
          if (!entry) return
          for (const type of entry.eventTypes) {
            push(anchor, row, type, type, false, entry.note)
          }
        }

        if (row.schedule.monthly) emitAnchored('monthly')
        if (row.schedule.quarterly && month % 3 === 0) emitAnchored('quarterly')
        if (row.schedule.semiannually && month % 6 === 0) emitAnchored('semiannually')
        if (row.schedule.yearly && month === hashString(row.equipmentType) % 12) {
          emitAnchored('yearly')
        }
      }
    }

    // Non-routine calendar events → pass through on their exact dates.
    for (const ev of row.calendar?.events ?? []) {
      const ts = parseISO(ev.date)
      if (ts < startTs || ts >= endTs) continue
      const eventType = NON_ROUTINE_EVENT_TYPE[ev.event] ?? 'preventive_maintenance'
      push(ts, row, eventType, ev.event, true, ev.note)
    }
  }

  return out.sort((a, b) => a.date.localeCompare(b.date) || a.equipmentType.localeCompare(b.equipmentType))
}
